import memoize from 'memoizee';
import { contract as currentContract, isContractDataLoaded } from '@/contract';
import { arrayToHashmap } from '@/utils/Array';
import { multiCall } from '../Calls';
import { liquiditygauge_abi, liquiditygauge_abi_v2, isFactoryAbiDataLoaded } from '@/constants/abis';
import { getNonFactoPools, isFactoryPoolDataLoaded, getFactoryV2Pools, getFactoryCryptoPools } from '@/constants/pools';
import { sleepUntil } from '@/utils/Async';

const CRV_TOKEN_DECIMALS = 18;

/**
 * Retrieves the amount of claimable crv per gauge.
 *
 * Returns object of shape { compound: AMOUNT, iearn: AMOUNT, … }
 */
const getClaimableCRV = memoize(async (userAddress, includeFactoryV2 = false) => {
    await sleepUntil(() => isFactoryPoolDataLoaded() && isFactoryAbiDataLoaded() && isContractDataLoaded());

    const allPools = includeFactoryV2 ? getNonFactoPools().concat(getFactoryV2Pools()).concat(getFactoryCryptoPools()) : getNonFactoPools();
    const poolsWithGauges = allPools.filter(({ hasNoGauge }) => !hasNoGauge);

    // V3 gauges don't have claimable CRV, return an empty result set
    const poolsWithNonV3Gauges = poolsWithGauges.filter(({ gaugeVersion }) => gaugeVersion !== 3);
    const areAllGaugesVersion3 = poolsWithNonV3Gauges.length === 0;
    if (areAllGaugesVersion3) {
        const claimableCRVPerGauge = arrayToHashmap(poolsWithGauges.map(({ id }) => [id, 0]));
        return claimableCRVPerGauge;
    }

    const callsConfig = poolsWithNonV3Gauges.map(({ gaugeVersion, addresses: { gauge: gaugeAddress } }) => ({
        contract: new currentContract.web3.eth.Contract(
            gaugeVersion === 2 ? liquiditygauge_abi_v2 : liquiditygauge_abi,
            gaugeAddress
        ),
        methodName: 'claimable_tokens',
        params: [userAddress],
    }));

    const claimableCRV = await multiCall(callsConfig);

    const claimableCRVPerGauge = arrayToHashmap(claimableCRV.map((amount, i) => [
        poolsWithNonV3Gauges[i].id,
        amount / (10 ** CRV_TOKEN_DECIMALS),
    ]));

    return claimableCRVPerGauge;
}, {
    promise: true,
    maxAge: 10 * 60 * 1000, // 10m
    length: 2, // Take into account second optional param for cache busting
});

export default getClaimableCRV;
